import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

export default function useTranslation(...extraNamespaces) {
  const lang = useSelector((state) => state.globalSettings.lang); // ✅ Ensure correct Redux path
  const [translations, setTranslations] = useState({});

  useEffect(() => {
    if (!lang) return; // Prevent execution if lang is not set

    const loadTranslations = async () => {
      try {
        // Load common.json dynamically based on the selected language
        const commonResponse = await import(`@/locales/${lang}/common.json`);
        const commonTranslations = commonResponse.default;

        // Load extra namespaces dynamically
        const namespacePromises = extraNamespaces.map(async (ns) => {
          try {
            const response = await import(`@/locales/${lang}/${ns}.json`);
            return { [ns]: response.default };
          } catch (error) {
            console.error(`Translation file missing: ${lang}/${ns}.json`);
            return {}; // Avoid breaking on missing files
          }
        });

        const loadedNamespaces = await Promise.all(namespacePromises);

        // Merge all translations
        setTranslations({
          ...commonTranslations, // ✅ Dynamically loaded common.json
          ...Object.assign({}, ...loadedNamespaces), // ✅ Merge dynamically loaded namespaces
        });
      } catch (error) {
        console.error('Error loading translations:', error);
      }
    };

    loadTranslations();
  }, [lang, ...extraNamespaces]); // ✅ Ensure effect re-runs when language changes

  // Translation function
  const translate = (key, values = {}) => {
    const [namespace, textKey] = key.includes(':')
      ? key.split(':')
      : [null, key];
    const text = namespace
      ? translations[namespace]?.[textKey]
      : translations[textKey];

    if (!text) {
      console.warn(`Missing translation for key: ${key}`);
      return key; // Fallback to raw key if translation is missing
    }

    return Object.keys(values).reduce(
      (result, placeholder) =>
        result.replace(`{{${placeholder}}}`, values[placeholder]),
      text,
    );
  };

  return translate;
}
